import * as React from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";

import AutocompleteFormField from "components/autocomplete-form-field";
import { PAYMENT_SOURCE_TYPE } from "constants/patients";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type PaymentSourceOption = { label: string; value: string };

type SelectPaymentSourceTypeProps = {
  onOptionSelected?: (option: PaymentSourceOption | PaymentSourceOption[] | null) => void;
  enablePortal?: boolean;
  multiple?: boolean;
  sx?: SxProps;
};

export type RhfSelectPaymentSourceType<T extends FieldValues> =
  SelectPaymentSourceTypeProps & UseControllerProps<T>;

export default function SelectPaymentSourceType<T extends FieldValues>({
  control,
  name,
  onOptionSelected,
  enablePortal,
  multiple = false,
  sx = {}
}: RhfSelectPaymentSourceType<T>) {
  const filterOptions = createFilterOptions({
    matchFrom: "start",
    stringify: (option: { label: string; value: string }) => option.label
  });

  return (
    <AutocompleteFormField
      enablePortal={enablePortal}
      openOnFocus
      autoHighlight
      autoFocus
      name={name}
      label="By Payment Source"
      filterOptions={filterOptions}
      options={PAYMENT_SOURCE_TYPE.getOptions()}
      multiple={multiple}
      getOptionLabel={(option) => {
        return option?.label ?? "";
      }}
      renderOption={(props: object, option: any, { selected }: any) => (
        <ListItem {...props}>
          {multiple ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
              <ListItemText>{option.label}</ListItemText>
            </Stack>
          ) : (
            <ListItemText>{option.label}</ListItemText>
          )}
        </ListItem>
      )}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      onOptionSelected={(option: any) => {
        if (onOptionSelected) {
          onOptionSelected(option);
        }
      }}
      sx={{ ml: 0.5, mt: 0, minWidth: multiple ? 220 : 200, ...sx }}
    />
  );
}
